import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  Alert,
  useTheme,
} from '@mui/material';
import ComputerIcon from '@mui/icons-material/Computer';
import InventoryIcon from '@mui/icons-material/Inventory';
import PersonIcon from '@mui/icons-material/Person';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useLanguage } from '../contexts/LanguageContext';

const STATUS_PIE_COLORS = ['#8b8bff', '#5ee7f1', '#f472b6', '#fbbf24', '#34d399', '#60a5fa', '#fb7185'];

interface DashboardData {
  counts: { total: number; inStock: number; issued: number; writtenOff: number };
  byType: { type: string; count: number }[];
  assignmentsByDay: { day: string; issues: number; returns: number }[];
  recentOperations: { type: string; date: string; device: string; person: string }[];
}

type StatKey = 'total' | 'inStock' | 'issued' | 'writtenOff';

interface StatDef {
  key: StatKey;
  label: string;
  route: string;
  icon: React.ReactElement;
  gradient: string;
  shadow: string;
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const locale = useMemo(() => language === 'ru' ? ru : enUS, [language]);

  const statDefs = useMemo<StatDef[]>(() => {
    return [
      {
        key: 'total' as StatKey,
        label: t('dashboard.statTotal'),
        route: '/devices',
        icon: <ComputerIcon />,
        gradient: isDark
          ? 'linear-gradient(135deg, #8b8bff 0%, #5b5bf6 100%)'
          : 'linear-gradient(135deg, #5b5bf6 0%, #8b8bff 100%)',
        shadow: isDark
          ? '0 14px 36px -14px rgba(139,139,255,0.6)'
          : '0 14px 36px -14px rgba(91,91,246,0.45)',
      },
      {
        key: 'inStock' as StatKey,
        label: t('dashboard.statInStock'),
        route: '/devices?status=in_stock',
        icon: <InventoryIcon />,
        gradient: isDark
          ? 'linear-gradient(135deg, #34d399 0%, #0ea5a5 100%)'
          : 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
        shadow: isDark
          ? '0 14px 36px -14px rgba(52,211,153,0.55)'
          : '0 14px 36px -14px rgba(16,185,129,0.4)',
      },
      {
        key: 'issued' as StatKey,
        label: t('dashboard.statIssued'),
        route: '/devices?status=issued',
        icon: <PersonIcon />,
        gradient: isDark
          ? 'linear-gradient(135deg, #f59e0b 0%, #f472b6 100%)'
          : 'linear-gradient(135deg, #f59e0b 0%, #ec4899 100%)',
        shadow: isDark
          ? '0 14px 36px -14px rgba(245,158,11,0.55)'
          : '0 14px 36px -14px rgba(245,158,11,0.4)',
      },
      {
        key: 'writtenOff' as StatKey,
        label: t('dashboard.statWrittenOff'),
        route: '/devices?status=written_off',
        icon: <DeleteSweepIcon />,
        gradient: isDark
          ? 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)'
          : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        shadow: isDark
          ? '0 14px 36px -14px rgba(248,113,113,0.55)'
          : '0 14px 36px -14px rgba(239,68,68,0.4)',
      },
    ];
  }, [isDark, t]);

  useEffect(() => {
    api.get('/dashboard')
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.error || t('dashboard.errorLoading')))
      .finally(() => setLoading(false));
  }, [t]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return <Alert severity="error" sx={{ borderRadius: 1.5 }}>{error || t('dashboard.noData')}</Alert>;
  }

  const { counts, byType, assignmentsByDay, recentOperations } = data;

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 5)  return t('dashboard.greetingNight');
    if (h < 12) return t('dashboard.greetingMorning');
    if (h < 18) return t('dashboard.greetingAfternoon');
    return t('dashboard.greetingEvening');
  })();

  return (
    <Box>
      {/* Welcome strip */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="overline" sx={{ color: 'var(--text-muted)' }}>
          {format(new Date(), 'EEEE, d MMMM', { locale })}
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mt: 0.5 }}>
          {greeting}. <span className="gradient-text">{t('dashboard.systemOverview')}</span>
        </Typography>
        <Typography variant="body1" sx={{ color: 'var(--text-muted)', mt: 0.5 }}>
          {t('dashboard.keyMetrics')}
        </Typography>
      </Box>

      {/* Stat cards */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {statDefs.map((s) => (
          <Grid item xs={12} sm={6} md={3} key={s.key}>
            <Card sx={{ p: 0, overflow: 'hidden' }}>
              <CardActionArea
                onClick={() => navigate(s.route)}
                sx={{
                  p: 0,
                  display: 'block',
                  '& .MuiCardActionArea-focusHighlight': { background: 'transparent' },
                }}
              >
                <CardContent sx={{ p: 3, display: 'flex', gap: 2.5, alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      background: s.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      boxShadow: s.shadow,
                      flexShrink: 0,
                      '& svg': { fontSize: 28 },
                    }}
                  >
                    {s.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1 }}
                    >
                      {counts[s.key]}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-muted)', mt: 0.5 }}>
                      {s.label}
                    </Typography>
                  </Box>
                  <ArrowOutwardIcon
                    sx={{ fontSize: 18, color: 'var(--text-subtle)', flexShrink: 0 }}
                  />
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} lg={7}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
                    {t('dashboard.assignmentsByDay')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
                    {t('dashboard.assignmentsByDayDesc')}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${assignmentsByDay.reduce((s, d) => s + d.issues + d.returns, 0)} ${t('dashboard.events')}`}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={assignmentsByDay} margin={{ top: 10, right: 8, left: -12, bottom: 0 }} barCategoryGap="20%">
                  <defs>
                    <linearGradient id="issueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={isDark ? '#8b8bff' : '#5b5bf6'} stopOpacity={1} />
                      <stop offset="100%" stopColor={isDark ? '#5ee7f1' : '#22c3d1'} stopOpacity={0.7} />
                    </linearGradient>
                    <linearGradient id="returnGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={isDark ? '#34d399' : '#10b981'} stopOpacity={1} />
                      <stop offset="100%" stopColor={isDark ? '#5ee7f1' : '#14b8a6'} stopOpacity={0.6} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 6"
                    vertical={false}
                    stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'}
                  />
                  <XAxis
                    dataKey="day"
                    tickFormatter={(d: string) => format(parseISO(d), 'dd.MM', { locale })}
                    tick={{ fill: isDark ? '#a2a8c0' : '#55607a', fontSize: 11 }}
                    axisLine={{ stroke: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.1)' }}
                    tickLine={false}
                    interval="preserveStartEnd"
                    minTickGap={16}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: isDark ? '#a2a8c0' : '#55607a', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)' }}
                    contentStyle={{
                      background: isDark ? '#141623' : '#ffffff',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.1)'}`,
                      borderRadius: 10,
                      boxShadow: isDark ? '0 8px 24px rgba(0,0,0,.5)' : '0 8px 24px rgba(15,23,42,.08)',
                      color: isDark ? '#eceefc' : '#0b1020',
                    }}
                    labelStyle={{ color: isDark ? '#eceefc' : '#0b1020', fontWeight: 600 }}
                    itemStyle={{ color: isDark ? '#a2a8c0' : '#55607a' }}
                    labelFormatter={(label: string) => format(parseISO(label as string), 'dd.MM.yyyy', { locale })}
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    iconType="circle"
                    iconSize={8}
                    formatter={(value: string) => (
                      <span style={{ color: isDark ? '#a2a8c0' : '#55607a', fontSize: 12 }}>
                        {value}
                      </span>
                    )}
                  />
                  <Bar dataKey="issues" name={t('dashboard.chartIssues')} fill="url(#issueGrad)" radius={[4, 4, 1, 1]} maxBarSize={14} />
                  <Bar dataKey="returns" name={t('dashboard.chartReturns')} fill="url(#returnGrad)" radius={[4, 4, 1, 1]} maxBarSize={14} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
                  {t('dashboard.distributionByType')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
                  {t('dashboard.distributionByTypeDesc')}
                </Typography>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={byType}
                    dataKey="count"
                    nameKey="type"
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={110}
                    paddingAngle={3}
                    stroke="none"
                  >
                    {byType.map((_, idx) => (
                      <Cell key={idx} fill={STATUS_PIE_COLORS[idx % STATUS_PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: isDark ? '#141623' : '#ffffff',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.1)'}`,
                      borderRadius: 10,
                      boxShadow: isDark ? '0 8px 24px rgba(0,0,0,.5)' : '0 8px 24px rgba(15,23,42,.08)',
                      color: isDark ? '#eceefc' : '#0b1020',
                    }}
                    labelStyle={{ color: isDark ? '#eceefc' : '#0b1020', fontWeight: 600 }}
                    itemStyle={{ color: isDark ? '#a2a8c0' : '#55607a' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent operations */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
                {t('dashboard.recentOperations')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
                {t('dashboard.recentOperationsDesc')}
              </Typography>
            </Box>
            <Chip size="small" label={`${recentOperations.length} ${t('dashboard.events')}`} sx={{ fontWeight: 600 }} />
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, border: '1px solid var(--border)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('dashboard.tableType')}</TableCell>
                  <TableCell>{t('dashboard.tableDevice')}</TableCell>
                  <TableCell>{t('dashboard.tableEmployee')}</TableCell>
                  <TableCell align="right">{t('dashboard.tableDate')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentOperations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'var(--text-muted)' }}>
                      {t('dashboard.noOperations')}
                    </TableCell>
                  </TableRow>
                ) : (
                  recentOperations.map((op, idx) => {
                    const color: 'primary' | 'success' | 'error' =
                      op.type === 'Issue' ? 'primary' : op.type === 'Return' ? 'success' : 'error';
                    const typeLabel =
                      op.type === 'Issue' ? t('dashboard.opIssue') : op.type === 'Return' ? t('dashboard.opReturn') : t('dashboard.opWriteOff');
                    return (
                      <TableRow key={idx}>
                        <TableCell>
                          <Chip
                            label={typeLabel}
                            size="small"
                            color={color}
                            sx={{
                              background:
                                color === 'primary'
                                  ? isDark
                                    ? 'rgba(139,139,255,0.15)'
                                    : 'rgba(91,91,246,0.12)'
                                  : color === 'success'
                                  ? isDark
                                    ? 'rgba(52,211,153,0.15)'
                                    : 'rgba(16,185,129,0.12)'
                                  : isDark
                                  ? 'rgba(248,113,113,0.15)'
                                  : 'rgba(239,68,68,0.12)',
                              color: 'inherit',
                              border: 'none',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{op.device}</TableCell>
                        <TableCell sx={{ color: 'var(--text-muted)' }}>{op.person}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {format(parseISO(op.date), 'dd.MM.yyyy', { locale })}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}